import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Avatar,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  EmojiEvents,
  Edit,
} from '@mui/icons-material';
import unifiedApi from '../../utils/unifiedApi';
import { usePermissions } from '../../contexts/PermissionsContext';

const MatchReportViewDialog = ({ open, onClose, gameId, teamId, teamName, leagueId }) => {
  const navigate = useNavigate();
  const { canManageLeague } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  const canEdit = leagueId ? canManageLeague(leagueId) : false;

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch match report with all player details
      const data = await unifiedApi.get(`/api/games/${gameId}/teams/${teamId}/report`);
      
      if (!data) {
        throw new Error('Match report not found for this team');
      }

      setReportData(data);
    } catch (err) {
      console.error('Error fetching match report:', err);
      setError(err.message || 'Failed to load match report');
    } finally {
      setLoading(false);
    }
  }, [gameId, teamId]);

  useEffect(() => {
    if (open && gameId && teamId) {
      fetchReportData();
    }
  }, [open, gameId, teamId, fetchReportData]);

  // Position order for sorting
  const positionOrder = {
    'GK': 1,
    'K': 1, // Goalkeeper abbreviation
    'FB': 2,
    'D': 2, // Defender
    'MF': 3,
    'M': 3, // Midfielder
    'FW': 4,
    'F': 4, // Forward
    'A': 4, // Attacker
    'Spare': 5,
  };

  const getPositionSortValue = (position) => {
    if (!position) return 999; // Unknown positions last
    const upperPos = position.toUpperCase();
    return positionOrder[upperPos] || 999;
  };

  const sortPlayersByPosition = (players) => {
    return [...players].sort((a, b) => {
      // First separate by play status
      if (a.didPlay && !b.didPlay) return -1;
      if (!a.didPlay && b.didPlay) return 1;
      
      // Then by spare status
      if (a.didPlay && b.didPlay) {
        if (a.isSpare && !b.isSpare) return 1;
        if (!a.isSpare && b.isSpare) return -1;
      }
      
      // Then by position
      const posA = getPositionSortValue(a.position);
      const posB = getPositionSortValue(b.position);
      if (posA !== posB) return posA - posB;
      
      // Finally by last name
      const nameA = (a.lastName || '').toUpperCase();
      const nameB = (b.lastName || '').toUpperCase();
      return nameA.localeCompare(nameB);
    });
  };

  const getGameResult = () => {
    if (!reportData) return null;
    
    const teamGoals = reportData.isHomeTeam ? reportData.homeGoals : reportData.awayGoals;
    const opponentGoals = reportData.isHomeTeam ? reportData.awayGoals : reportData.homeGoals;
    
    if (teamGoals > opponentGoals) return 'Win';
    if (teamGoals < opponentGoals) return 'Loss';
    return 'Draw';
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'Win': return 'success';
      case 'Loss': return 'error';
      case 'Draw': return 'warning';
      default: return 'default';
    }
  };

  const renderPlayerRow = (player) => {
    const fullName = `${player.firstName} ${player.lastName}`;
    
    return (
      <TableRow 
        key={player.userTenantId}
        sx={{
          opacity: player.didPlay ? 1 : 0.5,
          textDecoration: player.didPlay ? 'none' : 'line-through',
        }}
      >
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {player.didPlay ? (
              <CheckCircle color="success" fontSize="small" />
            ) : (
              <Cancel color="disabled" fontSize="small" />
            )}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: player.didPlay ? 500 : 400 }}>
                {fullName}
              </Typography>
              {player.isSpare && player.didPlay && (
                <Chip label="Spare" size="small" color="warning" variant="outlined" sx={{ mt: 0.5 }} clickable={false} onClick={() => {}} />
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">
            {player.position || '-'}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body2">
            {player.shirtNumber || '-'}
          </Typography>
        </TableCell>
        <TableCell align="center">
          {player.goals > 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
              {Array.from({ length: player.goals }, (_, i) => (
                <span key={i} style={{ fontSize: '1.2em' }}>⚽</span>
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">-</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {player.yellowCards > 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
              {Array.from({ length: player.yellowCards }, (_, i) => (
                <span key={i} style={{ fontSize: '1.2em' }}>🟨</span>
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">-</Typography>
          )}
        </TableCell>
        <TableCell align="center">
          {player.redCards > 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
              {Array.from({ length: player.redCards }, (_, i) => (
                <span key={i} style={{ fontSize: '1.2em' }}>🟥</span>
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">-</Typography>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {reportData?.teamLogoUrl && (
            <Avatar 
              src={reportData.teamLogoUrl} 
              alt={reportData.teamName}
              sx={{ width: 48, height: 48 }}
            />
          )}
          <Box>
            <Typography variant="h6">{reportData?.teamName || teamName}</Typography>
            <Typography variant="caption" color="text.secondary">
              Match Report
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {!loading && !error && reportData && (
          <Box>
            {/* Game Result */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <Chip 
                icon={<EmojiEvents />}
                label={getGameResult()}
                color={getResultColor(getGameResult())}
                size="large"
                sx={{ fontSize: '1.1em', py: 2.5, px: 1 }}
                clickable={false}
                onClick={() => {}}
              />
              <Typography variant="h4" sx={{ mt: 2, mb: 1 }}>
                {reportData.homeGoals} - {reportData.awayGoals}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {reportData.homeTeamName} vs {reportData.awayTeamName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(reportData.gameDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Managers */}
            {reportData.managers && reportData.managers.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Team Managers
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {reportData.managers.map((manager, idx) => (
                    <Chip 
                      key={idx}
                      label={`${manager.firstName} ${manager.lastName}`}
                      size="small"
                      variant="outlined"
                      clickable={false}
                      onClick={() => {}}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Players Table */}
            <Typography variant="h6" gutterBottom>
              Player Appearances
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
              Crossed out players did not attend the match
            </Typography>
            
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Player</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Position</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>⚽ Goals</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>🟨 Yellow</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>🟥 Red</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortPlayersByPosition(reportData.players).map(renderPlayerRow)}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Match Notes */}
            {reportData.notes && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Match Notes
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {reportData.notes}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {canEdit && (
          <Button
            startIcon={<Edit />}
            onClick={() => {
              onClose();
              navigate(`/games/${gameId}/teams/${teamId}/report/edit`);
            }}
            variant="outlined"
          >
            Edit Report
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatchReportViewDialog;
